import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  XAxis,
  YAxis,
} from 'recharts';
import {
  IconActivity,
  IconChartBar,
  IconDatabase,
  IconRefresh,
} from '@tabler/icons-react';
import novaOpsApi from '@/lib/novaOpsApi';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { canonicalMsisdn } from './customerEdrUsageUtils';

type ServiceFilter = 'all' | 'data' | 'voice' | 'sms' | 'ussd';

type AttributedPlan = {
  planVersionId: string;
  planSubscriptionId: string | null;
  planName: string | null;
  validFrom: string | null;
  validTo: string | null;
  novaProduct: CustomerEdrProduct | null;
};

type CustomerEdrProduct = {
  id: string | null;
  name: string | null;
  code: string | null;
  typeCode: string | null;
  productKey: string | null;
  productValue: string | null;
  dataMb: number | null;
  usageTextData: string | null;
  usageTextVoice: string | null;
  usageTextSms: string | null;
};

type CustomerEdrEvent = {
  eventType: string;
  eventTimestamp: string;
  action: string;
  msisdn: string | null;
  requestType: string | null;
  sessionId: string | null;
  sessionStart: string | null;
  sessionEnd: string | null;
  durationSeconds: number | null;
  dataMb: number;
  dataUplinkMb: number;
  dataDownlinkMb: number;
  ratingGroup: string | null;
  partyNumber: string | null;
  chargedAmount: number | null;
  planVersionId: string | null;
  plansUsed: string[];
  plansReserved: string[];
  attributedPlans: AttributedPlan[];
  planSubscriptionId: string | null;
  planName: string | null;
  validFrom: string | null;
  validTo: string | null;
  balanceTypeId: string | null;
  balanceBeforeTotal: number;
  balanceBeforeTotalMb: number;
  balanceBeforeAvailable: number;
  balanceBeforeMb: number;
  balanceBeforeUsed: number;
  balanceBeforeUsedMb: number;
  balanceAfterTotal: number;
  balanceAfterTotalMb: number;
  balanceAfterAvailable: number;
  balanceAfterMb: number;
  balanceAfterUsed: number;
  balanceAfterUsedMb: number;
  changeRequest: number | null;
  adjustment: string | null;
  transactionId: string | null;
  novaProduct: CustomerEdrProduct | null;
  novaTransaction: {
    id: string | null;
    status: string | null;
    amount: number | null;
    paymentStatus: string | null;
    provisioningStatus: string | null;
    createdAt: string | null;
  } | null;
  novaSubscription: {
    status: string | null;
    startDate: string | null;
  } | null;
  enrichmentSource: string | null;
};

export type CustomerEdrBillUsageResponse = {
  source: {
    projectId: string;
    datasetId: string;
    viewId: string;
    tableRef: string;
    totogiAccountId: string;
    mvno: string;
    days: number;
    limit: number;
    eventTypeFilter: string;
    msisdnFilter: string | null;
    msisdnFilterActive: boolean;
    distinctMsisdns: string[];
  };
  summary: {
    totalEvents: number;
    byAction: Array<{ eventType: string; action: string; count: number }>;
    dailyUsage: Array<{
      date: string;
      eventType?: string;
      action?: string;
      dataMb: number;
      eventCount: number;
    }>;
  };
  events: CustomerEdrEvent[];
  migrationStatus?: {
    state: string;
    source: string;
    mvno: string;
    note?: string;
  };
};

type BundleSummary = {
  key: string;
  label: string;
  detail: string;
  activePlanCount: number;
  activeCategory: string | null;
  allocatedMb: number;
  dataMb: number;
  voiceSeconds: number;
  smsCount: number;
  ussdCount: number;
  expiredMb: number;
  remainingMb: number;
  validFrom: string | null;
  validTo: string | null;
  timeRemaining: string | null;
  latestEvent: string | null;
  source: 'active-plan' | 'name-match' | 'matched-plan' | 'plan-event' | 'balance-type' | 'unattributed';
};

type ActiveBalancePlanBundle = {
  planSubscriptionId?: string | null;
  name: string;
  category: string;
  remainingPercent: number;
  formattedTimeRemaining: string;
  formattedUsage: string;
  components?: Array<{
    label: string;
    category: string;
    remainingPercent: number;
    formattedUsage: string;
  }>;
};

type XdrBalancePlans = {
  aggregated: {
    airtime: XdrBalanceCategory;
    data: XdrBalanceCategory;
    voice: XdrBalanceCategory;
    sms: XdrBalanceCategory;
  };
  bundles: Record<'combo' | 'data' | 'social' | 'voice' | 'sms', ActiveBalancePlanBundle[]>;
};

type XdrBalanceCategory = {
  formatted: string;
  percentageUsed: number;
  value?: number;
  availableValue?: number;
  usedValue?: number;
  reservedValue?: number;
  totalValue?: number;
};

type ServiceBreakdown = {
  dataMb: number;
  voiceSeconds: number;
  smsCount: number;
  ussdCount: number;
};

type DailyBundleChartRow = {
  date: string;
  serviceBreakdown: Record<string, ServiceBreakdown>;
} & Record<string, string | number | Record<string, ServiceBreakdown>>;

const DAYS_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

const SERVICE_FILTER_OPTIONS: Array<{ value: ServiceFilter; label: string }> = [
  { value: 'all', label: 'All services' },
  { value: 'data', label: 'Data' },
  { value: 'voice', label: 'Voice' },
  { value: 'sms', label: 'SMS' },
  { value: 'ussd', label: 'USSD' },
];

const chartConfig = {
  value: {
    label: 'Usage',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig;

// Capped at MAX_VISIBLE_BUNDLES + 1 ('Other') so this palette never has to
// repeat a color across two distinct series in the same stacked bar.
const MAX_VISIBLE_BUNDLES = 7;

const OTHER_BUNDLE_COLOUR = '#94a3b8';

const bundleColours = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#db2777',
  '#65a30d',
];

function colourForBundleKey(key: string, index: number) {
  return key === 'Other' ? OTHER_BUNDLE_COLOUR : bundleColours[index % bundleColours.length];
}

function tenantQuery(mvno?: string | null) {
  return mvno ? `&mvno=${encodeURIComponent(mvno)}` : '';
}

function getApiErrorMessage(err: unknown, fallback: string) {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}

function formatChartDate(value: string | null | undefined) {
  if (!value) return '—';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
}

function formatDuration(seconds: number | null | undefined) {
  if (seconds === null || seconds === undefined || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function formatMb(value: number | null | undefined) {
  if (value === null || value === undefined || value <= 0) return '—';
  if (value >= 1024) return `${(value / 1024).toFixed(2)} GB`;
  return `${value.toFixed(2)} MB`;
}

function formatUsageBreakdown(breakdown: ServiceBreakdown) {
  const parts = [
    breakdown.dataMb > 0 ? `Data ${formatMb(breakdown.dataMb)}` : null,
    breakdown.voiceSeconds > 0 ? `Voice ${formatDuration(breakdown.voiceSeconds)}` : null,
    breakdown.smsCount > 0 ? `SMS ${breakdown.smsCount}` : null,
    breakdown.ussdCount > 0 ? `USSD ${breakdown.ussdCount}` : null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(' · ') : 'No usage';
}

function normalizeBundleName(value: string | null | undefined) {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TRAILING_DATE_SUFFIX_PATTERN = /[-_]\d{8}(?:-\d+)?$/;
const SNAKE_CASE_PATTERN = /^[a-z0-9_]+$/;

/**
 * Chart-only display label cleanup. Never used for matching/keying — only
 * for what gets printed in the legend, bar tooltip, and axis. Raw plan
 * subscription IDs and Totogi's date-suffixed internal plan names are
 * meaningless to a support agent and blow out legend width.
 */
function formatBundleDisplayLabel(rawLabel: string | null | undefined) {
  const trimmed = (rawLabel ?? '').trim();
  if (!trimmed) return 'Unnamed plan';

  if (UUID_PATTERN.test(trimmed)) {
    return `Unresolved plan · ${trimmed.slice(0, 8)}…`;
  }

  const withoutDateSuffix = trimmed.replace(TRAILING_DATE_SUFFIX_PATTERN, '');

  if (!SNAKE_CASE_PATTERN.test(withoutDateSuffix)) {
    return withoutDateSuffix;
  }

  return withoutDateSuffix
    .split('_')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

function parseUsageAmountToMb(value: string) {
  const match = value.match(/([\d.]+)\s*(kb|mb|gb|tb)\b/i);
  if (!match) return 0;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount)) return 0;
  const unit = match[2].toLowerCase();
  if (unit === 'kb') return amount / 1024;
  if (unit === 'gb') return amount * 1024;
  if (unit === 'tb') return amount * 1024 * 1024;
  return amount;
}

function parseActivePlanUsage(formattedUsage: string) {
  const [remainingRaw, totalRaw] = formattedUsage.split(/\s+of\s+/i);
  const remainingMb = parseUsageAmountToMb(remainingRaw ?? '');
  const allocatedMb = parseUsageAmountToMb(totalRaw ?? '');

  return {
    remainingMb,
    allocatedMb,
  };
}

function flattenActivePlanBundles(balancePlans?: XdrBalancePlans | null) {
  if (!balancePlans?.bundles) return [];

  return (Object.entries(balancePlans.bundles) as Array<
    [keyof XdrBalancePlans['bundles'], ActiveBalancePlanBundle[]]
  >).flatMap(([category, bundles]) =>
    bundles.map((bundle, index) => ({
      ...bundle,
      category,
      index,
      matchKey: normalizeBundleName(`${category}:${bundle.name}`),
      nameKey: normalizeBundleName(bundle.name),
    })),
  );
}

function dayKey(value: string | null | undefined) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateRange(days: number) {
  const end = new Date();
  end.setHours(0, 0, 0, 0);
  const start = new Date(end);
  start.setDate(start.getDate() - Math.max(0, days - 1));

  const values: string[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    values.push(dayKey(cursor.toISOString()) ?? cursor.toISOString().slice(0, 10));
    cursor.setDate(cursor.getDate() + 1);
  }
  return values;
}

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'R 0.00';
  }
  return `R ${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatMsisdnLocal(value: string | null | undefined) {
  if (!value) return '—';
  const canonical = canonicalMsisdn(value);
  if (!canonical) return value;
  if (canonical.startsWith('27') && canonical.length === 11) {
    return `0${canonical.slice(2)}`;
  }
  return canonical;
}

function serviceForEvent(event: CustomerEdrEvent): ServiceFilter {
  const searchable = [
    event.action,
    event.requestType,
    event.ratingGroup,
    event.balanceTypeId,
    event.planName,
    event.novaProduct?.name,
    event.novaProduct?.usageTextData,
    event.novaProduct?.usageTextVoice,
    event.novaProduct?.usageTextSms,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (searchable.includes('ussd')) return 'ussd';
  if (searchable.includes('sms')) return 'sms';
  if (searchable.includes('voice') || ((event.durationSeconds ?? 0) > 0 && event.dataMb <= 0)) {
    return 'voice';
  }
  return 'data';
}

function isUsageEvent(event: CustomerEdrEvent) {
  return event.eventType === 'CHRG' && event.action === 'charge';
}

function isAllocationEvent(event: CustomerEdrEvent) {
  return (
    event.eventType === 'ACNT' &&
    (event.action === 'subscribeToPlan' ||
      event.action === 'activatePlanSubscription' ||
      event.action === 'updateBalance')
  );
}

function isExpiryEvent(event: CustomerEdrEvent) {
  return event.eventType === 'ACNT' && event.action === 'expirePlanSubscription';
}

function eventLabel(event: CustomerEdrEvent) {
  if (event.eventType === 'CHRG' && event.action === 'charge') return 'Usage session';
  if (event.eventType === 'BILL' && event.action === 'charge') return 'Billed usage';
  if (event.eventType === 'BILL' && event.action === 'recurringFee') return 'Recurring fee';
  if (event.eventType === 'BILL' && event.action === 'purchaseFee') return 'Purchase fee';
  if (event.eventType === 'ACNT' && event.action === 'updateBalance') return 'Balance update';
  if (event.eventType === 'ACNT' && event.action === 'activatePlanSubscription') return 'Plan activated';
  if (event.eventType === 'ACNT' && event.action === 'subscribeToPlan') return 'Plan subscribed';
  if (event.eventType === 'ACNT' && event.action === 'expirePlanSubscription') return 'Bundle expired';
  return `${event.eventType}:${event.action}`;
}

function planLabelFromEvent(event: CustomerEdrEvent) {
  return (
    event.novaProduct?.name ??
    event.planName ??
    event.planSubscriptionId ??
    event.planVersionId ??
    event.balanceTypeId ??
    'Unattributed usage'
  );
}

function bundleKeyFromEvent(event: CustomerEdrEvent) {
  return (
    event.planSubscriptionId ??
    event.planVersionId ??
    event.balanceTypeId ??
    'unattributed'
  );
}

function attributedPlanLabel(plan: AttributedPlan) {
  return (
    plan.novaProduct?.name ??
    plan.planName ??
    plan.planSubscriptionId ??
    plan.planVersionId
  );
}

function allocatedMbFromEvent(event: CustomerEdrEvent) {
  return Math.max(
    event.novaProduct?.dataMb ?? 0,
    event.balanceBeforeTotalMb ?? 0,
    event.balanceAfterTotalMb ?? 0,
  );
}

function upsertBundle(
  bundles: Map<string, BundleSummary>,
  patch: Partial<BundleSummary> & Pick<BundleSummary, 'key' | 'label'>,
) {
  const existing = bundles.get(patch.key);
  const next: BundleSummary = {
    key: patch.key,
    label: patch.label,
    detail: patch.detail ?? existing?.detail ?? '',
    activePlanCount: (existing?.activePlanCount ?? 0) + (patch.activePlanCount ?? 0),
    activeCategory: patch.activeCategory ?? existing?.activeCategory ?? null,
    allocatedMb:
      patch.source === 'active-plan' && existing
        ? (existing.allocatedMb ?? 0) + (patch.allocatedMb ?? 0)
        : Math.max(existing?.allocatedMb ?? 0, patch.allocatedMb ?? 0),
    dataMb: (existing?.dataMb ?? 0) + (patch.dataMb ?? 0),
    voiceSeconds: (existing?.voiceSeconds ?? 0) + (patch.voiceSeconds ?? 0),
    smsCount: (existing?.smsCount ?? 0) + (patch.smsCount ?? 0),
    ussdCount: (existing?.ussdCount ?? 0) + (patch.ussdCount ?? 0),
    expiredMb: Math.max(existing?.expiredMb ?? 0, patch.expiredMb ?? 0),
    remainingMb:
      patch.source === 'active-plan' && existing
        ? (existing.remainingMb ?? 0) + (patch.remainingMb ?? 0)
        : Math.max(existing?.remainingMb ?? 0, patch.remainingMb ?? 0),
    validFrom: patch.validFrom ?? existing?.validFrom ?? null,
    validTo: patch.validTo ?? existing?.validTo ?? null,
    timeRemaining: patch.timeRemaining ?? existing?.timeRemaining ?? null,
    latestEvent:
      !existing?.latestEvent || (patch.latestEvent ?? '') > existing.latestEvent
        ? patch.latestEvent ?? existing?.latestEvent ?? null
        : existing.latestEvent,
    source:
      existing?.source === 'active-plan' && patch.source && patch.source !== 'active-plan'
        ? patch.source
        : patch.source ?? existing?.source ?? 'unattributed',
  };
  bundles.set(next.key, next);
}

function findActiveBundleKey(
  activeBundleKeysById: Map<string, string>,
  activeBundleKeysByName: Map<string, string>,
  label: string,
  ids: Array<string | null | undefined> = [],
) {
  for (const id of ids) {
    const key = id ? activeBundleKeysById.get(id) : null;
    if (key) return key;
  }

  const exact = activeBundleKeysByName.get(normalizeBundleName(label));
  if (exact) return exact;

  const normalized = normalizeBundleName(label);
  for (const [activeName, key] of activeBundleKeysByName.entries()) {
    if (activeName && normalized && (activeName.includes(normalized) || normalized.includes(activeName))) {
      return key;
    }
  }

  return null;
}

function buildBundleSummaries(
  events: CustomerEdrEvent[],
  balancePlans?: XdrBalancePlans | null,
) {
  const bundles = new Map<string, BundleSummary>();
  const activeBundleKeysById = new Map<string, string>();
  const activeBundleKeysByName = new Map<string, string>();

  for (const activeBundle of flattenActivePlanBundles(balancePlans)) {
    const { allocatedMb, remainingMb } = parseActivePlanUsage(activeBundle.formattedUsage);
    const key = activeBundle.planSubscriptionId
      ? `active:${activeBundle.planSubscriptionId}`
      : `active:${activeBundle.matchKey}`;
    if (activeBundle.planSubscriptionId) {
      activeBundleKeysById.set(activeBundle.planSubscriptionId, key);
    }
    activeBundleKeysByName.set(activeBundle.nameKey, key);

    upsertBundle(bundles, {
      key,
      label: activeBundle.name,
      detail: `${activeBundle.category} · ${activeBundle.formattedUsage}`,
      activePlanCount: 1,
      activeCategory: activeBundle.category,
      allocatedMb,
      remainingMb,
      timeRemaining: activeBundle.formattedTimeRemaining,
      latestEvent: null,
      source: 'active-plan',
    });
  }

  for (const event of events.filter((row) => isAllocationEvent(row) || isExpiryEvent(row))) {
    const label = planLabelFromEvent(event);
    const activeKey = findActiveBundleKey(activeBundleKeysById, activeBundleKeysByName, label, [
      event.planSubscriptionId,
      event.planVersionId,
      ...(event.plansUsed ?? []),
      ...(event.plansReserved ?? []),
    ]);
    const key = activeKey ?? bundleKeyFromEvent(event);
    upsertBundle(bundles, {
      key,
      label,
      detail: event.planSubscriptionId ?? event.planVersionId ?? event.balanceTypeId ?? '',
      allocatedMb: allocatedMbFromEvent(event),
      expiredMb: isExpiryEvent(event) ? event.balanceBeforeMb : 0,
      remainingMb: event.balanceAfterMb,
      validFrom: event.validFrom,
      validTo: event.validTo,
      latestEvent: event.eventTimestamp,
      source: activeKey
        ? 'name-match'
        : event.planVersionId || event.planSubscriptionId
          ? 'plan-event'
          : 'balance-type',
    });
  }

  for (const event of events.filter(isUsageEvent)) {
    const plans = event.attributedPlans?.length ? event.attributedPlans : [];
    if (plans.length === 0) {
      const label = planLabelFromEvent(event);
      const activeKey = findActiveBundleKey(activeBundleKeysById, activeBundleKeysByName, label, [
        event.planSubscriptionId,
        event.planVersionId,
        ...(event.plansUsed ?? []),
        ...(event.plansReserved ?? []),
      ]);
      const key = activeKey ?? bundleKeyFromEvent(event);
      upsertBundle(bundles, {
        key,
        label,
        detail: event.balanceTypeId ?? event.ratingGroup ?? '',
        ...usagePatchForEvent(event),
        latestEvent: event.eventTimestamp,
        source: activeKey
          ? 'name-match'
          : key === 'unattributed'
            ? 'unattributed'
            : 'balance-type',
      });
      continue;
    }

    for (const plan of plans) {
      const label = attributedPlanLabel(plan);
      const activeKey = findActiveBundleKey(activeBundleKeysById, activeBundleKeysByName, label, [
        plan.planSubscriptionId,
        plan.planVersionId,
      ]);
      const key = activeKey ?? plan.planSubscriptionId ?? plan.planVersionId;
      upsertBundle(bundles, {
        key,
        label,
        detail: plan.planSubscriptionId ?? plan.planVersionId,
        allocatedMb: plan.novaProduct?.dataMb ?? 0,
        ...usagePatchForEvent(event, plans.length),
        validFrom: plan.validFrom,
        validTo: plan.validTo,
        latestEvent: event.eventTimestamp,
        source: activeKey ? 'name-match' : 'matched-plan',
      });
    }
  }

  return [...bundles.values()]
    .map((bundle) => ({
      ...bundle,
      remainingMb:
        bundle.remainingMb > 0
          ? bundle.remainingMb
          : Math.max(0, bundle.allocatedMb - bundle.dataMb - bundle.expiredMb),
    }))
    .sort((a, b) => {
      if (a.source === 'active-plan' && b.source !== 'active-plan') return -1;
      if (b.source === 'active-plan' && a.source !== 'active-plan') return 1;
      return (b.latestEvent ?? '').localeCompare(a.latestEvent ?? '');
    });
}

function usagePatchForEvent(event: CustomerEdrEvent, divisor = 1): Partial<BundleSummary> {
  const service = serviceForEvent(event);
  const splitBy = Math.max(1, divisor);

  return {
    dataMb: service === 'data' ? event.dataMb / splitBy : 0,
    voiceSeconds:
      service === 'voice' ? (event.durationSeconds ?? 0) / splitBy : 0,
    smsCount: service === 'sms' ? 1 / splitBy : 0,
    ussdCount: service === 'ussd' ? 1 / splitBy : 0,
  };
}

function addServiceBreakdown(
  target: ServiceBreakdown,
  event: CustomerEdrEvent,
  divisor = 1,
) {
  const patch = usagePatchForEvent(event, divisor);
  target.dataMb += patch.dataMb ?? 0;
  target.voiceSeconds += patch.voiceSeconds ?? 0;
  target.smsCount += patch.smsCount ?? 0;
  target.ussdCount += patch.ussdCount ?? 0;
}

function emptyServiceBreakdown(): ServiceBreakdown {
  return {
    dataMb: 0,
    voiceSeconds: 0,
    smsCount: 0,
    ussdCount: 0,
  };
}

function emptyDailyBundleRow(date: string): DailyBundleChartRow {
  return { date, serviceBreakdown: {} };
}

function mergeBreakdownInto(target: ServiceBreakdown, source: ServiceBreakdown) {
  target.dataMb += source.dataMb;
  target.voiceSeconds += source.voiceSeconds;
  target.smsCount += source.smsCount;
  target.ussdCount += source.ussdCount;
}

function buildDailyBundleRows(
  events: CustomerEdrEvent[],
  bundles: BundleSummary[],
  days: number,
  serviceFilter: ServiceFilter,
) {
  const labels = new Map(bundles.map((bundle) => [bundle.key, bundle.label]));
  const bundleKeyByLabel = new Map(
    bundles
      .filter((bundle) => bundle.key !== 'unattributed')
      .map((bundle) => [normalizeBundleName(bundle.label), bundle.key]),
  );
  const resolveChartKey = (label: string, fallbackKey: string) => {
    const exact = bundleKeyByLabel.get(normalizeBundleName(label));
    if (exact) return exact;

    if (labels.has(fallbackKey)) return fallbackKey;
    return fallbackKey === 'unattributed' ? 'Other' : fallbackKey;
  };
  const rows = new Map<string, DailyBundleChartRow>();

  for (const date of dateRange(days)) {
    rows.set(date, emptyDailyBundleRow(date));
  }

  for (const event of events.filter(isUsageEvent)) {
    const day = dayKey(event.eventTimestamp);
    if (!day) continue;
    const current = rows.get(day) ?? emptyDailyBundleRow(day);
    const plans = event.attributedPlans?.length ? event.attributedPlans : [];
    if (plans.length === 0) {
      const key = resolveChartKey(planLabelFromEvent(event), bundleKeyFromEvent(event));
      if (!labels.has(key) && key !== 'Other') {
        labels.set(key, planLabelFromEvent(event));
      }
      const breakdown =
        current.serviceBreakdown[key] ?? emptyServiceBreakdown();
      addServiceBreakdown(breakdown, event);
      current.serviceBreakdown[key] = breakdown;
      current[key] = chartValueForService(breakdown, serviceFilter);
      rows.set(day, current);
      continue;
    }
    for (const plan of plans) {
      const key = plan.planSubscriptionId ?? plan.planVersionId;
      const label = attributedPlanLabel(plan);
      const chartKey = resolveChartKey(label, key);
      if (!labels.has(chartKey) && chartKey !== 'Other') {
        labels.set(chartKey, label);
      }
      const breakdown =
        current.serviceBreakdown[chartKey] ?? emptyServiceBreakdown();
      addServiceBreakdown(breakdown, event, plans.length);
      current.serviceBreakdown[chartKey] = breakdown;
      current[chartKey] = chartValueForService(breakdown, serviceFilter);
    }
    rows.set(day, current);
  }

  const rowList = [...rows.values()];
  const keysWithUsage = [...new Set([...labels.keys(), 'Other'])].filter((key) =>
    rowList.some((row) => {
      const breakdown = row.serviceBreakdown[key];
      return (
        Number(row[key] ?? 0) > 0 ||
        (breakdown?.voiceSeconds ?? 0) > 0 ||
        (breakdown?.smsCount ?? 0) > 0 ||
        (breakdown?.ussdCount ?? 0) > 0
      );
    }),
  );

  // Cap the number of stacked series: a support agent can't distinguish 16
  // colors in one bar. Rank real bundles by total volume in the current
  // metric and fold everything past the cap into the 'Other' bucket instead
  // of letting the legend (and the color palette) grow unbounded.
  const realKeys = keysWithUsage.filter((key) => key !== 'Other');
  const totalByKey = new Map(
    realKeys.map((key) => [key, rowList.reduce((sum, row) => sum + Number(row[key] ?? 0), 0)]),
  );
  const rankedRealKeys = [...realKeys].sort(
    (a, b) => (totalByKey.get(b) ?? 0) - (totalByKey.get(a) ?? 0),
  );
  const keptKeys = rankedRealKeys.slice(0, MAX_VISIBLE_BUNDLES);
  const overflowKeys = rankedRealKeys.slice(MAX_VISIBLE_BUNDLES);

  if (overflowKeys.length > 0) {
    for (const row of rowList) {
      const otherBreakdown = row.serviceBreakdown.Other ?? emptyServiceBreakdown();
      let otherValue = Number(row.Other ?? 0);
      for (const key of overflowKeys) {
        const breakdown = row.serviceBreakdown[key];
        if (breakdown) mergeBreakdownInto(otherBreakdown, breakdown);
        otherValue += Number(row[key] ?? 0);
        delete row.serviceBreakdown[key];
        delete row[key];
      }
      row.serviceBreakdown.Other = otherBreakdown;
      row.Other = otherValue;
    }
  }

  const finalKeys =
    overflowKeys.length > 0 || keysWithUsage.includes('Other')
      ? [...keptKeys, 'Other']
      : keptKeys;

  return {
    keys: finalKeys,
    labels: new Map(
      [...labels, ['Other', 'Other usage']].map(([key, label]) => [
        key,
        key === 'Other' ? 'Other usage' : formatBundleDisplayLabel(label),
      ]),
    ),
    rows: rowList.sort((a, b) => String(a.date).localeCompare(String(b.date))),
  };
}

function chartValueForService(
  breakdown: ServiceBreakdown,
  serviceFilter: ServiceFilter,
) {
  if (serviceFilter === 'voice') return breakdown.voiceSeconds / 60;
  if (serviceFilter === 'sms') return breakdown.smsCount;
  if (serviceFilter === 'ussd') return breakdown.ussdCount;
  return breakdown.dataMb;
}

function chartMetricLabel(serviceFilter: ServiceFilter) {
  if (serviceFilter === 'voice') return 'Daily voice usage by bundle';
  if (serviceFilter === 'sms') return 'Daily SMS usage by bundle';
  if (serviceFilter === 'ussd') return 'Daily USSD usage by bundle';
  return 'Daily data usage by bundle';
}

function balanceCategoryLabel(key: keyof XdrBalancePlans['aggregated']) {
  if (key === 'airtime') return 'Airtime';
  if (key === 'data') return 'Data';
  if (key === 'voice') return 'Voice';
  return 'SMS';
}

function getBalanceSegments(balance: XdrBalanceCategory) {
  const total = Math.max(0, Number(balance.totalValue ?? 0));
  const available = Math.max(
    0,
    Number(balance.availableValue ?? balance.value ?? 0),
  );
  const used = Math.max(
    0,
    Number(
      balance.usedValue ??
        (total > 0 ? (balance.percentageUsed / 100) * total : 0),
    ),
  );
  const reserved = Math.max(
    0,
    Number(balance.reservedValue ?? Math.max(0, total - available - used)),
  );
  const scale = Math.max(total, available + reserved + used);
  const toPercent = (value: number) =>
    scale > 0 ? Math.min(100, Math.max(0, (value / scale) * 100)) : 0;

  return {
    availablePercent: toPercent(available),
    reservedPercent: toPercent(reserved),
  };
}

function BalanceSegmentBar({ balance }: { balance: XdrBalanceCategory }) {
  const segments = getBalanceSegments(balance);

  return (
    <div className="space-y-2">
      <div className="flex h-2 overflow-hidden rounded-full bg-secondary">
        <div
          className="bg-blue-600"
          style={{ width: `${segments.availablePercent}%` }}
        />
        <div
          className="bg-amber-400"
          style={{ width: `${segments.reservedPercent}%` }}
        />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm bg-blue-600" />
          Available
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="size-2 rounded-sm bg-amber-400" />
          Temporarily allocated
        </span>
      </div>
    </div>
  );
}

function bundleMatchLabel(source: BundleSummary['source']) {
  if (source === 'active-plan') return 'Active plan';
  if (source === 'name-match') return 'Active + XDR';
  if (source === 'matched-plan') return 'XDR plan matched';
  if (source === 'plan-event') return 'XDR plan event';
  if (source === 'balance-type') return 'XDR balance type';
  return 'Unmatched';
}

function bundleMatchVariant(source: BundleSummary['source']) {
  if (source === 'name-match' || source === 'matched-plan') return 'success';
  if (source === 'active-plan') return 'info';
  if (source === 'unattributed') return 'secondary';
  return 'outline';
}

function InfoBlock({
  title,
  value,
  detail,
  tone = 'default',
}: {
  title: string;
  value: string;
  detail: string;
  tone?: 'default' | 'warning' | 'success';
}) {
  const toneClass =
    tone === 'warning'
      ? 'border-amber-200 bg-amber-50'
      : tone === 'success'
        ? 'border-green-200 bg-green-50'
        : 'border-border bg-background';

  return (
    <div className={`rounded-md border p-4 ${toneClass}`}>
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <p className="mt-2 text-xl font-semibold">{value}</p>
      <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
    </div>
  );
}

function DailyBundleTooltip({
  active,
  label,
  payload,
}: {
  active?: boolean;
  label?: string | number;
  payload?: Array<{
    dataKey?: string | number;
    name?: string | number;
    payload?: DailyBundleChartRow;
  }>;
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row) return null;

  const entries = payload
    .map((item) => {
      const key = String(item.dataKey ?? '');
      if (!key) return null;
      const breakdown = row.serviceBreakdown[key];
      if (!breakdown) return null;
      const hasUsage =
        breakdown.dataMb > 0 ||
        breakdown.voiceSeconds > 0 ||
        breakdown.smsCount > 0 ||
        breakdown.ussdCount > 0;
      if (!hasUsage) return null;

      return {
        key,
        label: String(item.name ?? key),
        breakdown,
      };
    })
    .filter((item): item is {
      key: string;
      label: string;
      breakdown: ServiceBreakdown;
    } => Boolean(item));

  if (entries.length === 0) return null;

  return (
    <div className="min-w-[280px] rounded-md border bg-background p-3 text-sm shadow-lg">
      <p className="mb-2 font-semibold">{formatChartDate(String(label ?? ''))}</p>
      <div className="space-y-2">
        {entries.map((entry) => (
          <div key={entry.key} className="grid grid-cols-[1fr_auto] gap-3">
            <span className="min-w-0 truncate text-muted-foreground">
              {entry.label}
            </span>
            <span className="text-right font-medium">
              {formatUsageBreakdown(entry.breakdown)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface CustomerEdrUsagePanelProps {
  iCustomer?: number | null;
  accountUuid?: string | null;
  mvno?: string | null;
  balancePlans?: XdrBalancePlans | null;
  enabled?: boolean;
}

export function CustomerEdrUsagePanel({
  iCustomer,
  accountUuid,
  mvno,
  balancePlans,
  enabled = true,
}: CustomerEdrUsagePanelProps) {
  const [days, setDays] = useState('30');
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>('all');
  const [data, setData] = useState<CustomerEdrBillUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setServiceFilter('all');
    setData(null);
  }, [accountUuid, iCustomer]);

  const fetchUsage = useCallback(async () => {
    if (!iCustomer || !enabled) return;
    setLoading(true);
    setError('');
    try {
      const canonicalSelector = accountUuid
        ? `&account_uuid=${encodeURIComponent(accountUuid)}`
        : '';
      const res = await novaOpsApi.get<CustomerEdrBillUsageResponse>(
        `/ops/customers/edr-xdr-usage?i_customer=${iCustomer}${canonicalSelector}&days=${days}&limit=500${tenantQuery(mvno)}`,
      );
      setData(res.data ?? null);
    } catch (err: unknown) {
      console.error('Failed to fetch EDR XDR usage:', err);
      setData(null);
      setError(getApiErrorMessage(err, 'Failed to load Totogi XDR usage from BigQuery'));
    } finally {
      setLoading(false);
    }
  }, [accountUuid, days, enabled, iCustomer, mvno]);

  useEffect(() => {
    void fetchUsage();
  }, [fetchUsage]);

  const visibleEvents = useMemo(() => {
    const events = data?.events ?? [];
    if (serviceFilter === 'all') return events;
    return events.filter((event) => !isUsageEvent(event) || serviceForEvent(event) === serviceFilter);
  }, [data?.events, serviceFilter]);

  const usageEvents = useMemo(
    () => visibleEvents.filter(isUsageEvent),
    [visibleEvents],
  );

  const serviceCounts = useMemo(() => {
    const counts: Record<ServiceFilter, number> = {
      all: usageEvents.length,
      data: 0,
      voice: 0,
      sms: 0,
      ussd: 0,
    };
    for (const event of usageEvents) counts[serviceForEvent(event)] += 1;
    return counts;
  }, [usageEvents]);

  const bundles = useMemo(
    () => buildBundleSummaries(visibleEvents, balancePlans),
    [balancePlans, visibleEvents],
  );
  const dailyBundleUsage = useMemo(
    () => buildDailyBundleRows(usageEvents, bundles, Number(days), serviceFilter),
    [bundles, days, serviceFilter, usageEvents],
  );

  const stats = useMemo(() => {
    const totalDataMb = usageEvents.reduce((sum, event) => sum + event.dataMb, 0);
    const totalCallSeconds = usageEvents.reduce(
      (sum, event) =>
        serviceForEvent(event) === 'voice'
          ? sum + (event.durationSeconds ?? 0)
          : sum,
      0,
    );
    const totalCharged = visibleEvents.reduce(
      (sum, event) => sum + (event.chargedAmount ?? 0),
      0,
    );
    const largestCharge = [...visibleEvents]
      .filter((event) => (event.chargedAmount ?? 0) > 0)
      .sort((a, b) => (b.chargedAmount ?? 0) - (a.chargedAmount ?? 0))[0] ?? null;

    return {
      totalDataMb,
      totalCallSeconds,
      totalCharged,
      largestCharge,
    };
  }, [usageEvents, visibleEvents]);

  const bundleTotals = useMemo(
    () => ({
      allocatedMb: bundles.reduce((sum, bundle) => sum + bundle.allocatedMb, 0),
      dataMb: bundles.reduce((sum, bundle) => sum + bundle.dataMb, 0),
      voiceSeconds: bundles.reduce((sum, bundle) => sum + bundle.voiceSeconds, 0),
      smsCount: bundles.reduce((sum, bundle) => sum + bundle.smsCount, 0),
      ussdCount: bundles.reduce((sum, bundle) => sum + bundle.ussdCount, 0),
      expiredMb: bundles.reduce((sum, bundle) => sum + bundle.expiredMb, 0),
      remainingMb: bundles.reduce((sum, bundle) => sum + bundle.remainingMb, 0),
    }),
    [bundles],
  );

  const daysLabel = DAYS_OPTIONS.find((option) => option.value === days)?.label ?? `${days} days`;
  const serviceSummaryCard = (
    <Card className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {SERVICE_FILTER_OPTIONS.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={serviceFilter === option.value ? 'default' : 'outline'}
            className="h-8 rounded-full px-3"
            onClick={() => setServiceFilter(option.value)}
          >
            {option.label}
            <span className="ml-2 text-xs opacity-70">
              {serviceCounts[option.value]}
            </span>
          </Button>
        ))}
      </div>
      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-md border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">CDRs</p>
          <p className="mt-1 text-2xl font-semibold">{usageEvents.length}</p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Data</p>
          <p className="mt-1 text-2xl font-semibold">{formatMb(stats.totalDataMb)}</p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Call time</p>
          <p className="mt-1 text-2xl font-semibold">{formatDuration(stats.totalCallSeconds)}</p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Charged</p>
          <p className="mt-1 text-2xl font-semibold">{formatCurrency(stats.totalCharged)}</p>
        </div>
      </div>
    </Card>
  );
  const dailyUsageChart = (
    <Card className="p-4">
      <div className="mb-4 flex items-center gap-2">
        <IconChartBar className="size-4" />
        <h4 className="font-semibold">{chartMetricLabel(serviceFilter)}</h4>
      </div>
      {dailyBundleUsage.rows.length > 0 ? (
        <ChartContainer config={chartConfig} className="h-[320px] w-full">
          <BarChart data={dailyBundleUsage.rows}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatChartDate}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={18}
            />
            <YAxis tickLine={false} axisLine={false} width={54} />
            <ChartTooltip content={<DailyBundleTooltip />} />
            {dailyBundleUsage.keys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={dailyBundleUsage.labels.get(key) ?? key}
                stackId="usage"
                radius={index === dailyBundleUsage.keys.length - 1 ? [4, 4, 0, 0] : 0}
              >
                {dailyBundleUsage.rows.map((row) => (
                  <Cell
                    key={`${row.date}-${key}`}
                    fill={colourForBundleKey(key, index)}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ChartContainer>
      ) : (
        <p className="text-sm text-muted-foreground">No daily usage data in this period.</p>
      )}
      <div className="mt-3 flex flex-wrap gap-3">
        {dailyBundleUsage.keys.map((key, index) => (
          <div key={key} className="flex items-center gap-2 text-xs text-muted-foreground">
            <span
              className="size-3 rounded-sm"
              style={{ backgroundColor: colourForBundleKey(key, index) }}
            />
            {dailyBundleUsage.labels.get(key) ?? key}
          </div>
        ))}
      </div>
    </Card>
  );

  if (!iCustomer) {
    return (
      <Alert>
        <AlertDescription>
          Search for a customer account to inspect Totogi usage and XDR events.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <IconActivity className="size-5" />
            <h3 className="text-lg font-semibold">Usage &amp; XDR</h3>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Customer-care view for usage, top-ups, expired bundles, and billing events.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={days}
            onValueChange={(value) => {
              if (value) setDays(value);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Lookback">{daysLabel}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {DAYS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void fetchUsage()}
            disabled={loading}
          >
            <IconRefresh className={`mr-1 size-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading && !data ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Loading Totogi XDR usage from BigQuery...
        </Card>
      ) : null}

      {data ? (
        <>
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <IconDatabase className="size-4" />
              <span>BigQuery EDR source</span>
              <Badge variant="outline">Full XDR</Badge>
              <Badge variant="secondary">{visibleEvents.length} shown</Badge>
              {data.source.msisdnFilterActive ? (
                <Badge variant="secondary" className="text-xs">
                  Line filter: {formatMsisdnLocal(data.source.msisdnFilter)}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-xs">All account events</Badge>
              )}
            </div>
          </Card>

          {balancePlans?.aggregated ? (
            <Card className="p-4">
              <div className="mb-4">
                <h4 className="font-semibold">Current balance &amp; active plans</h4>
                <p className="text-sm text-muted-foreground">
                  Active balances are shown alongside XDR usage so care can compare what is currently allocated with what was consumed.
                </p>
              </div>
              <div className="grid gap-3 md:grid-cols-4">
                {(Object.entries(balancePlans.aggregated) as Array<
                  [keyof XdrBalancePlans['aggregated'], XdrBalancePlans['aggregated'][keyof XdrBalancePlans['aggregated']]]
                >).map(([key, balance]) => {
                  return (
                    <div key={key} className="rounded-md border p-3">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="min-w-0 text-sm font-bold">
                          {balanceCategoryLabel(key)} - {balance.formatted}
                        </p>
                        <p className="shrink-0 text-xs text-muted-foreground">
                          {balance.percentageUsed.toFixed(0)}% used
                        </p>
                      </div>
                      <div className="mt-2">
                        <BalanceSegmentBar balance={balance} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          ) : null}

          {serviceSummaryCard}

          {dailyUsageChart}

          <Card className="p-4">
            <div className="mb-4">
              <h4 className="font-semibold">Care questions by bundle</h4>
              <p className="text-sm text-muted-foreground">
                Pick the specific bundle the customer is asking about, then check allocation, usage, expiry, and remaining balance on that row.
              </p>
            </div>
            <div className="mb-4 grid gap-3 md:grid-cols-4">
              <InfoBlock
                title="Total allocated"
                value={formatMb(bundleTotals.allocatedMb)}
                detail={`${bundles.length} bundle${bundles.length === 1 ? '' : 's'} in this view`}
                tone={bundleTotals.allocatedMb > 0 ? 'success' : 'warning'}
              />
              <InfoBlock
                title="Total used"
                value={formatMb(bundleTotals.dataMb)}
                detail={`Voice ${formatDuration(bundleTotals.voiceSeconds)} · SMS ${bundleTotals.smsCount} · USSD ${bundleTotals.ussdCount}`}
              />
              <InfoBlock
                title="Total expired"
                value={formatMb(bundleTotals.expiredMb)}
                detail={
                  bundleTotals.expiredMb > 0
                    ? 'Expired balance was detected from Totogi bundle expiry events.'
                    : 'No unused bundle expiry amount found in this view.'
                }
                tone={bundleTotals.expiredMb > 0 ? 'warning' : 'success'}
              />
              <InfoBlock
                title="Airtime charged"
                value={formatCurrency(stats.totalCharged)}
                detail={
                  stats.largestCharge
                    ? `${eventLabel(stats.largestCharge)} · ${formatDuration(stats.largestCharge.durationSeconds)} · ${stats.largestCharge.partyNumber ?? 'no B-number'}`
                    : `Voice time: ${formatDuration(stats.totalCallSeconds)}`
                }
              />
            </div>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bundle to investigate</TableHead>
                    <TableHead>Allocated</TableHead>
                    <TableHead>Used</TableHead>
                    <TableHead>Expired</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>Answer for care</TableHead>
                    <TableHead>Validity</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bundles.length > 0 ? (
                    bundles.map((bundle) => {
                      const hasUsage =
                        bundle.dataMb > 0 ||
                        bundle.voiceSeconds > 0 ||
                        bundle.smsCount > 0 ||
                        bundle.ussdCount > 0;
                      return (
                        <TableRow key={bundle.key}>
                          <TableCell className="min-w-[260px]">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{bundle.label}</span>
                              {bundle.activePlanCount > 1 ? (
                                <Badge variant="outline" className="text-[10px]">
                                  {bundle.activePlanCount} active
                                </Badge>
                              ) : null}
                            </div>
                            <div className="font-mono text-xs text-muted-foreground">
                              {bundle.detail || bundle.key}
                            </div>
                          </TableCell>
                          <TableCell>{formatMb(bundle.allocatedMb)}</TableCell>
                          <TableCell>{formatUsageBreakdown(bundle)}</TableCell>
                          <TableCell>{formatMb(bundle.expiredMb)}</TableCell>
                          <TableCell>{formatMb(bundle.remainingMb)}</TableCell>
                          <TableCell className="min-w-[260px] text-sm text-muted-foreground">
                            {hasUsage
                              ? bundle.source === 'name-match' || bundle.source === 'matched-plan'
                                ? 'Active allocation and matching XDR usage were found.'
                                : 'XDR usage was found for this bundle.'
                              : bundle.allocatedMb > 0
                                ? 'Allocated, but no matching usage was found in this period.'
                                : 'Usage found, but allocation was not matched in this period.'}
                            {bundle.expiredMb > 0
                              ? ` ${formatMb(bundle.expiredMb)} expired unused.`
                              : ''}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatDate(bundle.validFrom)} - {formatDate(bundle.validTo)}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No bundle rows found for this filter.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card className="p-4">
            <div className="mb-4">
              <h4 className="font-semibold">Bundle usage</h4>
              <p className="text-sm text-muted-foreground">
                Usage is matched to Totogi plan IDs when available. Unmatched rows are grouped by balance type.
              </p>
            </div>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Bundle</TableHead>
                    <TableHead>Allocated</TableHead>
                    <TableHead>Used</TableHead>
                    <TableHead>Expired</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>Validity</TableHead>
                    <TableHead>Match</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bundles.length > 0 ? (
                    bundles.map((bundle) => (
                      <TableRow key={bundle.key}>
                        <TableCell className="min-w-[260px]">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{bundle.label}</span>
                            {bundle.activePlanCount > 1 ? (
                              <Badge variant="outline" className="text-[10px]">
                                {bundle.activePlanCount} active
                              </Badge>
                            ) : null}
                          </div>
                          <div className="font-mono text-xs text-muted-foreground">{bundle.detail || bundle.key}</div>
                        </TableCell>
                        <TableCell>{formatMb(bundle.allocatedMb)}</TableCell>
                        <TableCell>{formatUsageBreakdown(bundle)}</TableCell>
                        <TableCell>{formatMb(bundle.expiredMb)}</TableCell>
                        <TableCell>{formatMb(bundle.remainingMb)}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDate(bundle.validFrom)} - {formatDate(bundle.validTo)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={bundleMatchVariant(bundle.source) as any}>
                            {bundleMatchLabel(bundle.source)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No bundle usage or allocation rows found for this filter.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <details className="rounded-lg border bg-card p-4">
            <summary className="cursor-pointer font-semibold">Technical XDR details</summary>
            <div className="mt-4 space-y-4">
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Volume</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Party / B-number</TableHead>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Charged</TableHead>
                      <TableHead>Charging session</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usageEvents.length > 0 ? (
                      usageEvents.map((event, index) => (
                        <TableRow key={`${event.eventTimestamp}-${event.sessionId ?? index}`}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatDateTime(event.eventTimestamp)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{serviceForEvent(event).toUpperCase()}</Badge>
                          </TableCell>
                          <TableCell>{formatMb(event.dataMb)}</TableCell>
                          <TableCell>{formatDuration(event.durationSeconds)}</TableCell>
                          <TableCell className="font-mono text-xs">
                            {event.partyNumber ?? '—'}
                          </TableCell>
                          <TableCell className="max-w-[280px] truncate">
                            {event.attributedPlans?.length
                              ? event.attributedPlans.map(attributedPlanLabel).join(', ')
                              : planLabelFromEvent(event)}
                          </TableCell>
                          <TableCell>{formatCurrency(event.chargedAmount)}</TableCell>
                          <TableCell className="max-w-[260px] truncate font-mono text-xs">
                            {event.sessionId ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground">
                          No usage rows match the current filter.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>MSISDN</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Rating group</TableHead>
                      <TableHead>Plan IDs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleEvents.length > 0 ? (
                      visibleEvents.map((event, index) => (
                        <TableRow key={`${event.eventTimestamp}-${event.action}-${index}`}>
                          <TableCell className="whitespace-nowrap text-sm">
                            {formatDateTime(event.eventTimestamp)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{event.eventType}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{eventLabel(event)}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {event.msisdn ? formatMsisdnLocal(event.msisdn) : '—'}
                          </TableCell>
                          <TableCell>{formatMb(event.dataMb)}</TableCell>
                          <TableCell className="text-xs">{event.ratingGroup ?? '—'}</TableCell>
                          <TableCell className="max-w-[360px] truncate font-mono text-xs">
                            {[event.planSubscriptionId, event.planVersionId, ...(event.plansUsed ?? [])]
                              .filter(Boolean)
                              .join(', ') || '—'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No XDR events match the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </details>
        </>
      ) : null}
    </div>
  );
}
